import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart2, Plus, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Polls() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/polls").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const vote = async (pollId, optId) => { await api.post(`/polls/${pollId}/vote`, { option_id: optId }); load(); };
  const del = async (id) => { if (!window.confirm("Delete poll?")) return; await api.delete(`/polls/${id}`); load(); };
  return (
    <div className="space-y-6" data-testid="polls-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Team Voice</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Polls & Surveys</h1>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-poll" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> New poll</button>}
      </div>
      <div className="space-y-4">
        {rows.map((p) => {
          const total = (p.options || []).reduce((a, o) => a + (o.count || 0), 0);
          return (
            <div key={p.id} className="card-surface p-5" data-testid={`poll-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
                    <span>{p.kind}</span>{p.anonymous && <span className="inline-flex items-center gap-1 text-[color:var(--accent)]"><EyeOff size={10} /> Anonymous</span>}
                    <span>· {p.created_by_name}</span>
                  </div>
                  <h3 className="text-lg font-bold mt-1">{p.question}</h3>
                </div>
                {canManage && <button onClick={() => del(p.id)} data-testid={`poll-del-${p.id}`} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={14} /></button>}
              </div>
              <div className="space-y-2 mt-3">
                {p.options.map((o) => {
                  const pct = total > 0 ? Math.round(((o.count || 0) / total) * 100) : 0;
                  const mine = p.my_vote === o.id;
                  return (
                    <button
                      type="button"
                      key={o.id}
                      onClick={() => vote(p.id, o.id)}
                      data-testid={`poll-opt-${p.id}-${o.id}`}
                      className={`w-full relative overflow-hidden text-left bg-[#0b0d12] border rounded-lg p-3 hover:border-[color:var(--accent)] transition-colors ${mine ? "border-[color:var(--accent)]" : "border-[#2a3447]"}`}
                    >
                      <div className="absolute inset-y-0 left-0 opacity-15" style={{ background: "var(--accent)", width: `${pct}%` }} />
                      <div className="relative flex items-center justify-between text-sm">
                        <span className={mine ? "font-bold text-[color:var(--accent)]" : "text-slate-200"}>{o.label}</span>
                        <span className="text-xs text-slate-400">{o.count || 0} · {pct}%</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">{total} vote{total === 1 ? "" : "s"}</div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="card-surface p-10 text-center text-slate-500"><BarChart2 size={24} className="inline mr-2" /> No polls yet.</div>}
      </div>
      {open && <NewPoll onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewPoll({ onClose, onDone }) {
  const [f, setF] = useState({ question: "", optionsText: "", anonymous: false, kind: "poll" });
  const submit = async (e) => {
    e.preventDefault();
    const options = f.optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (options.length < 2) return toast.error("Add at least 2 options");
    await api.post("/polls", { question: f.question, options, anonymous: f.anonymous, kind: f.kind });
    onDone(); toast.success("Poll created");
  };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="poll-modal">
        <h2 className="text-xl font-bold mb-4">New poll</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Question *</div><input required value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="poll-question" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Options (one per line, min 2)</div><textarea rows={4} required value={f.optionsText} onChange={(e) => setF({ ...f, optionsText: e.target.value })} data-testid="poll-options" className={inp + " resize-none"} /></label>
        <div className="flex items-center justify-between mb-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.anonymous} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} data-testid="poll-anon" className="accent-[color:var(--accent)]" /> Anonymous</label>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="poll-kind" className="bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 text-xs"><option value="poll">Live poll</option><option value="survey">Survey</option></select>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button><button type="submit" data-testid="poll-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Create</button></div>
      </form>
    </div>
  );
}
